import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { relatedProducts } from '../../../services/product';

const API = (process.env.REACT_APP_API).replace(/\/api$/, '');

const RelatedProducts = ({ productId }) => {
  const [related, setRelated] = useState([]);
  const [showPrevBtn, setShowPrevBtn] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const lastManualScrollRef = useRef(0);

  const loadRelated = async () => {
    try {
      const res = await relatedProducts(productId);
      setRelated(res.data || []);
    } catch (error) {
      console.log('Erro ao carregar produtos relacionados:', error);
      setRelated([]);
    }
  };

  useEffect(() => {
    if (productId) {
      loadRelated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleScrollEvent = () => {
    if (!carouselRef.current) return;
    checkScrollButtons();
    lastManualScrollRef.current = Date.now();
    setIsAutoPlaying(false);
    
    // Retoma autoplay após 4 segundos sem scroll manual
    if (autoPlayRef.current) {
      clearTimeout(autoPlayRef.current);
    }
    autoPlayRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 4000);
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.addEventListener('scroll', handleScrollEvent);
    return () => carousel.removeEventListener('scroll', handleScrollEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkScrollButtons();
    if (related.length > 0 && isAutoPlaying) {
      startAutoPlay();
    }
    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
        clearInterval(autoPlayRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [related, isAutoPlaying]);

  const checkScrollButtons = () => {
    // Setas sempre visíveis
    setShowPrevBtn(true);
    setShowNextBtn(true);
  };

  const scroll = (direction) => {
    if (!carouselRef.current) return;
    const carousel = carouselRef.current;
    const scrollAmount = 240;
    const { scrollLeft, scrollWidth, clientWidth } = carousel;
    const maxScroll = scrollWidth - clientWidth;

    // Se chegou ao final, volta ao início
    if (direction === 'right' && scrollLeft >= maxScroll - 5) {
      carousel.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else if (direction === 'left' && scrollLeft <= 5) {
      // Se chegou ao início indo para trás, volta ao final
      carousel.scrollTo({
        left: maxScroll,
        behavior: 'smooth',
      });
    } else {
      carousel.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const startAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    autoPlayRef.current = setInterval(() => {
      if (!carouselRef.current) return;

      const carousel = carouselRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = carousel;
      const scrollAmount = 240;
      const maxScroll = scrollWidth - clientWidth;

      // Se chegou ao final, volta ao início suavemente
      if (scrollLeft >= maxScroll - 5) {
        carousel.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
      } else {
        carousel.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    }, 3500); // 3.5 segundos entre cada scroll automático
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Duplicar produtos para criar looping infinito
  const getDisplayProducts = () => {
    if (!related || related.length === 0) return [];
    const activeProducts = related.filter(p => p.status === 'active' && p.author && p.location);
    if (activeProducts.length === 0) return [];
    
    // Repetir os produtos 3x para preencher a tela e criar looping
    return [...activeProducts, ...activeProducts, ...activeProducts];
  };

  const displayProducts = getDisplayProducts();

  if (!displayProducts || displayProducts.length === 0) return null;

  return (
    <div className='related-products-wrapper'>
      <h3 className='related-products-title'>Produtos Relacionados</h3>

      <div className='carousel-wrapper'>
        {showPrevBtn && (
          <button
            className='carousel-arrow carousel-arrow-prev'
            onClick={() => scroll('left')}
            aria-label='Anterior'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='arrow-icon'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
            </svg>
          </button>
        )}

        <div className='carousel-container' ref={carouselRef}>
          {displayProducts.map((p, index) => {
            return (
              <Link
                key={`${p._id}-${index}`}
                to={`/product/${p._id}`}
                className='product-card'
              >
                <div className='product-card-inner'>
                  <div className='product-image'>
                    <img
                      src={p.images[0] ? `${API}${p.images[0]}` : '/default-image.jpg'}
                      alt={p.name}
                    />
                  </div>
                  <div className='product-info'>
                    <h4 className='product-name'>{p.name}</h4>
                    <p className='product-description'>{p.location.name}</p>
                    <div className='product-footer'>
                      <span className='product-price'>{formatCurrency(p.price)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {showNextBtn && (
          <button
            className='carousel-arrow carousel-arrow-next'
            onClick={() => scroll('right')}
            aria-label='Próximo'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='arrow-icon'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default RelatedProducts;
